import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Star, Calendar, MessageCircle, HelpCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

const GuestAuthorsPage = () => {
  const { api, isAdmin } = useAuth();
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAuthors = async () => {
      try {
        const res = await api.get('/guest-authors');
        setAuthors(res.data);
      } catch (error) {
        // Non-critical error suppressed
      } finally {
        setLoading(false);
      }
    };

    fetchAuthors();
  }, [api]);

  const getEventStatus = (author) => {
    const now = new Date();
    const start = new Date(author.qa_start_date);
    const end = new Date(author.qa_end_date);

    if (now < start) return 'upcoming';
    if (now > end) return 'past';
    return 'active';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return <Badge className="bg-green-100 text-green-700">Q&A Open</Badge>;
      case 'upcoming': return <Badge className="bg-blue-100 text-blue-700">Coming Soon</Badge>;
      case 'past': return <Badge className="bg-gray-100 text-gray-700">Archived</Badge>;
      default: return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="guest-authors-page">
      {/* Header */}
      <div className="relative mb-12">
        <div className="absolute inset-0 rounded-2xl overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1510442650500-93217e634e4c"
            alt="Guest Authors"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/90 to-brand-primary/70" />
        </div>
        <div className="relative p-8 md:p-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="text-white">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-6 h-6 text-brand-tertiary" />
                <span className="overline text-brand-tertiary">Meet the Authors</span>
              </div>
              <h1 className="font-serif text-4xl font-semibold mb-4">Guest Authors</h1>
              <p className="text-white/80 max-w-lg">
                Q&A sessions with professional authors. Ask questions, learn from their 
                experiences, and get inspired by the best in the craft.
              </p>
            </div>
            {isAdmin && (
              <Link to="/admin/guest-authors/new" data-testid="add-guest-author-button">
                <Button className="bg-white text-brand-primary hover:bg-white/90 gap-2">
                  <Star className="w-4 h-4" />
                  Add Guest Author
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Authors Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 gap-8">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}
        </div>
      ) : authors.length === 0 ? (
        <div className="text-center py-20">
          <Star className="w-16 h-16 text-text-muted mx-auto mb-4" />
          <h2 className="font-serif text-2xl font-semibold text-text-primary mb-2">No guest authors yet</h2>
          <p className="text-text-secondary">Check back soon for exciting Q&A sessions with professional authors!</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-8">
          {authors.map((author) => {
            const status = getEventStatus(author);
            return (
              <Link 
                key={author.id} 
                to={`/guest-authors/${author.id}`}
                className="group"
                data-testid={`author-card-${author.id}`}
              >
                <Card className="h-full bg-white border-brand-primary/10 overflow-hidden card-hover">
                  <CardContent className="p-0">
                    <div className="flex">
                      {author.photo_url ? (
                        <div className="w-1/3 min-h-[200px]">
                          <img 
                            src={author.photo_url}
                            alt={author.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-1/3 min-h-[200px] bg-brand-primary/10 flex items-center justify-center">
                          <Star className="w-12 h-12 text-brand-primary/30" />
                        </div>
                      )}
                      <div className="flex-1 p-6">
                        <div className="flex items-center justify-between mb-3">
                          {getStatusBadge(status)}
                          {author.is_live_event && (
                            <span className="text-xs px-2 py-1 rounded-full bg-brand-accent/10 text-brand-accent">
                              Live Event
                            </span>
                          )}
                        </div>

                        <h3 className="font-serif text-xl font-semibold text-text-primary mb-2 group-hover:text-brand-accent transition-colors">
                          {author.name}
                        </h3>

                        <p className="text-text-secondary text-sm line-clamp-3 mb-4">
                          {author.bio}
                        </p>

                        {author.notable_works && author.notable_works.length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs text-text-muted mb-1">Notable Works:</p>
                            <p className="text-sm text-text-secondary italic line-clamp-1">
                              {author.notable_works.join(', ')}
                            </p>
                          </div>
                        )}

                        <div className="flex items-center justify-between pt-4 border-t border-brand-primary/10 text-sm text-text-muted">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(author.qa_start_date).toLocaleDateString()} - {new Date(author.qa_end_date).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <HelpCircle className="w-4 h-4" />
                            {author.question_count} questions
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GuestAuthorsPage;
